<template>
  <div>
    <div
      id="edit-intruction-panel"
      class="fixed w-screen top-18 flex justify-end transition-all duration-500"
      :style="{
        right: open ? '0vw' : '-100vw',
        height: 'calc(100vh - 4.5rem)',
        zIndex: 51
      }"
    >
      <div
        class="h-full w-screen absolute left-0 top-0 -z-10"
        @click="$emit('close-panel')"
      ></div>
      <div
        class="h-full w-5 rounded-l-3xl flex-shrink-0 relative z-0"
        :style="{
          background: 'linear-gradient(180deg, #FFE4E8 0%, #9A8AC2 100%)'
        }"
      ></div>
      <div
        class="h-full w-120 flex-shrink-0 flex flex-col bg-pure-white relative z-0"
      >
        <div class="py-4 px-6 flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <div
              class="relative rounded-full flex-shrink-0 min-h-6 px-3 flex items-center"
              :class="[
                stage.is_discontinued
                  ? 'bg-rose-200'
                  : stage.is_admitted
                  ? 'bg-lime-200'
                  : 'bg-cornflower-200'
              ]"
            >
              <BlTextSubtitle3
                :text="stage.name"
                :class="[
                  stage.is_discontinued
                    ? 'text-rose-700'
                    : stage.is_admitted
                    ? 'text-lime-700'
                    : 'text-cornflower-800'
                ]"
              />
            </div>
            <BlIcon
              v-if="open"
              icon="close"
              color="slate-6"
              class="h-6 w-6"
              clickable
              @clicked="$emit('close-panel')"
            />
          </div>
          <BlTextTitle4 text="Stage instructions" />
        </div>
        <div class="px-6 py-4 rounded-t-3xl bg-light-purple-300">
          <BlTextSubtitle2 text="Edit instruction" class="text-pure-white" />
        </div>
        <div
          class="relative h-full flex flex-col justify-between overflow-y-auto"
        >
          <div class="py-5 px-6 flex flex-col gap-3">
            <BlInputText :input="titleInput" @changed="inputHandler" />
            <div v-if="open" class="pb-px relative">
              <VueEditor
                :editor-toolbar="customToolbar"
                v-model="descriptionInput"
                class="blum-editor"
                placeholder="Description"
              />
            </div>
            <div
              v-if="uploads.length"
              class="p-2 flex flex-col gap-2 rounded bg-slate-200"
            >
              <BlFileHolder
                v-for="upload in uploads"
                :key="upload.id"
                :name="upload.blob.filename"
                :size="upload.blob.byte_size"
                removeable
                @remove-handler="openRemoveDoc(upload)"
              />
            </div>
            <div class="flex items-center justify-between">
              <input
                type="file"
                :name="`upload_document_stage_${instruction.id}`"
                :id="`upload_document_stage_${instruction.id}`"
                @change="onAddDoc"
                hidden
              />
              <div class="flex items-center gap-4">
                <BlInputButton
                  v-if="open"
                  type="plain"
                  dense
                  icon="plus"
                  label="Add document"
                  :loading="uploadFileLoading"
                  @clicked="openUpload"
                />
                <div v-if="uploadFileLoading" class="blum-loader-xs"></div>
              </div>
              <BlTextBody2
                v-if="uploads.length"
                :text="`${uploads.length} file(s) added`"
              />
            </div>
          </div>
          <div
            class="h-18 px-6 pt-4 sticky bottom-0 flex-shrink-0 bg-pure-white grid grid-cols-3 gap-4"
          >
            <div class="flex gap-4">
              <BlInputButton
                v-if="open"
                icon="delete"
                type="plain-error"
                @clicked="deleteInstructionOpened = true"
              />
              <BlInputButton
                label="Close"
                type="plain"
                @clicked="$emit('close-panel')"
              />
            </div>
            <BlInputButton
              class="col-span-2"
              label="Update"
              :disabled="isDisabledSubmit"
              :loading="submitLoading"
              @clicked="onUpdate"
            />
          </div>
        </div>
      </div>
    </div>
    <BlBackdrop
      v-if="deleteFileOpened"
      title="Delete document"
      label="Delete"
      type="error"
      :disabled="deleteFileLoading"
      @backdrop-close="deleteFileOpened = false"
      @backdrop-action="onRemoveDoc(deleteFile)"
    >
      <div class="w-160 px-8">
        <BlTextBody2 text="You are about to delete " />
        <BlTextBody2
          :text="`${deleteFile.blob.filename}. `"
          class="font-bold"
        />
        <BlTextBody2
          text="Are you sure you want to proceed? This action is irreversible."
        />
      </div>
    </BlBackdrop>
    <BlBackdrop
      v-if="deleteInstructionOpened"
      title="Delete instruction"
      label="Delete"
      type="error"
      :disabled="deleteInstructionLoading"
      @backdrop-close="deleteInstructionOpened = false"
      @backdrop-action="onRemove"
    >
      <div class="w-160 px-8">
        <BlTextBody2 text="You are about to delete " />
        <BlTextBody2 :text="`${instruction.title}. `" class="font-bold" />
        <BlTextBody2
          text="Are you sure you want to proceed? This action is irreversible."
        />
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { VueEditor } from "vue2-editor";

import BlIcon from "../../BlIcon.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputText from "../../BlInputText.vue";
import BlFileHolder from "../../BlFileHolder.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "EditInstructionStagePanel",
  components: {
    VueEditor,
    BlIcon,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputText,
    BlFileHolder,
    BlInputButton,
    BlBackdrop
  },
  props: {
    open: { type: Boolean, default: false },
    instruction: { type: Object, required: true },
    stage: { type: Object, required: true }
  },
  data() {
    return {
      submitLoading: false,
      titleInput: {
        placeholder: "Title",
        model: ""
      },
      descriptionInput: "",
      customToolbar: [["bold", "italic", "underline", "link"]],
      uploads: [],
      uploadFileLoading: false,
      deleteFileLoading: false,
      deleteFileOpened: false,
      deleteFile: null,
      deleteInstructionOpened: false,
      deleteInstructionLoading: false
    };
  },
  computed: {
    isDisabledSubmit() {
      if (!this.titleInput.model) return true;
      return false;
    }
  },
  watch: {
    open() {
      this.resetInputs();
    },
    instruction: {
      deep: true,
      handler() {
        this.resetInputs();
      }
    }
  },
  methods: {
    ...mapActions([
      "updateInstruction",
      "deleteInstruction",
      "uploadDocumentInstruction",
      "deleteDocumentInstruction"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    resetInputs() {
      this.titleInput.model = this.instruction.title;
      this.descriptionInput = this.instruction.description;
      this.uploads = this.instruction.documents || [];
    },
    openUpload() {
      const input = document.getElementById(
        `upload_document_stage_${this.instruction.id}`
      );
      if (input) input.click();
    },
    onAddDoc(e) {
      const files = e.target.files;
      if (files && files.length) {
        const formData = new FormData();
        formData.append(`documents`, files[0]);
        const data = {
          id: this.instruction.id,
          file: formData
        };
        this.uploadFileLoading = true;
        this.uploadDocumentInstruction(data).then(({ success }) => {
          this.uploadFileLoading = false;
          if (!success) return;
          this.resetInputs();
        });
      }
    },
    openRemoveDoc(upload) {
      this.deleteFile = upload;
      this.deleteFileOpened = true;
    },
    async onRemoveDoc(upload) {
      const data = {
        id: this.instruction.id,
        file_id: upload.id
      };
      this.deleteFileLoading = true;
      return this.deleteDocumentInstruction(data).then(({ success }) => {
        this.deleteFileLoading = false;
        if (!success) return;
        this.deleteFileOpened = false;
        this.deleteFile = null;
        this.resetInputs();
      });
    },
    onUpdate() {
      const jsonData = {
        id: this.instruction.id,
        instruction: {
          title: this.titleInput.model,
          description: this.descriptionInput
        }
      };
      const data = jsonData;
      this.submitLoading = true;
      this.updateInstruction(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.$emit("close-panel");
      });
    },
    onRemove() {
      const data = {
        id: this.instruction.id,
        instructionable_id: this.stage.id,
        instructionable_type: "Stage"
      };
      this.deleteInstructionLoading = true;
      this.deleteInstruction(data).then(({ success }) => {
        this.deleteInstructionLoading = false;
        if (!success) return;
        this.deleteInstructionOpened = false;
        this.$emit("close-panel");
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/EditInstructionStagePanel.vue